/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext } from 'react';

import { BulkUserCreationData } from '../BulkUserCreation';
import { BULK_USER_TRACK_EVENTS } from '../BulkUserCreation.decl';
import { BulkUserCreationContext } from '../BulkUserCreationProvider/BulkUserCreationProvider';

import { Spacer, Typography } from '@aircall/tractor-v2';
import { ExternalLink } from '@components/Link';
import { Dropzone, FileInput, FormWizard, useFormWizard } from '@dashboard/library';
import { useTracker } from '@hooks/useTracker';
import { Trans, useTranslation } from 'react-i18next';

export function BulkUserCreationUploadFile() {
  const { t } = useTranslation();
  const { navigateToNextPage, navigateToPrevPage, data, editData } =
    useFormWizard<BulkUserCreationData>();
  const context = useContext(BulkUserCreationContext);
  const { track } = useTracker();

  const handleDrop = useCallback(
    (files: File[]) => {
      /* istanbul ignore next */
      if (!files?.length) {
        return;
      }

      const file = files[0];

      editData({ file });

      track({
        event: BULK_USER_TRACK_EVENTS.FILE_SELECTED,
        payload: { ...context.state, file_format: 'csv', file_size: file.size },
      });
    },
    [context.state, editData, track]
  );

  const handleDropRejected = useCallback(() => {
    track({ event: BULK_USER_TRACK_EVENTS.FILE_REJECTED, payload: context.state });
  }, [context.state, track]);

  const handleRemove = useCallback(() => {
    editData({ file: null });
  }, [editData]);

  const handleNext = useCallback(() => {
    navigateToNextPage();
    track({
      event: BULK_USER_TRACK_EVENTS.FILE_SUBMITTED,
      payload: { ...context.state, file_format: 'csv', file_size: data.file?.size },
    });
  }, [context.state, data.file?.size, navigateToNextPage, track]);

  const handlePrevious = useCallback(() => {
    editData({ file: null });
    navigateToPrevPage();
  }, [navigateToPrevPage, editData]);

  return (
    <>
      <FormWizard.PageHeader>
        <FormWizard.PageHeaderTitle>
          {t('bulk_user_creation.upload_users_info.title')}
        </FormWizard.PageHeaderTitle>
        <FormWizard.PageHeaderSubtitle>
          <Trans i18nKey='bulk_user_creation.page_header_subtitle'>
            <ExternalLink href={t('bulk_user_creation.prepare_file.title_link')} />
          </Trans>
        </FormWizard.PageHeaderSubtitle>
      </FormWizard.PageHeader>

      <FormWizard.PageContent data-test='upload-file'>
        <Spacer direction='vertical' space='m' fluid>
          {!data?.file ? (
            <Dropzone
              data-test='bulk-user-creation-dropzone'
              dropzoneOptions={{
                onDrop: handleDrop,
                onDropRejected: handleDropRejected,
                maxFiles: 1,
                accept: { 'text/csv': ['.csv'] },
                maxSize: 50000000,
              }}
              text={
                <Typography variant='bodyMediumM'>
                  <Trans i18nKey='bulk_user_creation.upload_users_info.dropzone_text'>
                    <u />
                  </Trans>
                </Typography>
              }
              subtext={t('bulk_user_creation.upload_users_info.dropzone_subtext')}
            />
          ) : (
            <FileInput
              data-test='bulk-user-creation-file-input'
              file={data.file}
              onRemove={handleRemove}
            />
          )}
          <Typography variant='supportingRegularXS' color='text-secondary'>
            {t('bulk_user_creation.upload_users_info.end_caption')}
          </Typography>
        </Spacer>
      </FormWizard.PageContent>

      <FormWizard.PageFooter>
        <FormWizard.PageFooterPrev onClick={handlePrevious}>
          {t('bulk_user_creation.upload_users_info.prev')}
        </FormWizard.PageFooterPrev>
        <FormWizard.PageFooterNext onClick={handleNext} disabled={!data.file}>
          {t('bulk_user_creation.upload_users_info.next')}
        </FormWizard.PageFooterNext>
      </FormWizard.PageFooter>

      <FormWizard.PageSubText>
        <Trans i18nKey='bulk_user_creation.contact_support'>
          <ExternalLink href={t('bulk_user_creation.contact_support_link')} />
        </Trans>
      </FormWizard.PageSubText>
    </>
  );
}
